// ═══════════════════════════════════════════════════════════════
// Incident List — Table view matching dashboard style
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconPlus, IconCircleCheck } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import type { Incident } from '../../auth/auth-store';
import { INCIDENT_TYPE_META } from './incident-helpers';
import './IncidentList.css';

// ── Helpers ───────────────────────────────────────────────────

function elapsed(startDate?: string): string {
  if (!startDate) return '';
  const ms = Date.now() - new Date(startDate).getTime();
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `T+${d}D ${h}H`;
  return `T+${h}H`;
}

function severityColor(sev?: string): { bg: string; text: string } {
  switch ((sev || '').toLowerCase()) {
    case 'critical': return { bg: '#ede9fe', text: '#4B3FFF' };
    case 'high':     return { bg: '#fee2e2', text: '#D33423' };
    case 'medium':   return { bg: '#fef3c7', text: '#885604' };
    case 'low':      return { bg: '#d1fae5', text: '#037730' };
    default:         return { bg: '#f1f5f9', text: '#7B7D7F' };
  }
}

const STATUS_ORDER: Record<string, number> = { active: 0, monitoring: 1, closed: 2, archived: 3 };

// ── Main Component ────────────────────────────────────────────

export default function IncidentList() {
  const navigate = useNavigate();
  const { incidents, loadIncidents, activeIncident, setActiveIncident } = useAuthStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  useEffect(() => { loadIncidents(); }, []);

  const statusCounts = useMemo(() => ({
    all:        incidents.length,
    active:     incidents.filter(i => i.status === 'active').length,
    monitoring: incidents.filter(i => i.status === 'monitoring').length,
    closed:     incidents.filter(i => i.status === 'closed').length,
  }), [incidents]);

  const filtered = useMemo(() => {
    let list = incidents;
    if (statusFilter !== 'all') list = list.filter(i => i.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.commander || '').toLowerCase().includes(q) ||
        (i.location || '').toLowerCase().includes(q) ||
        (i.incidentType || '').toLowerCase().includes(q)
      );
    }
    const base = [...list].sort((a, b) => {
      const diff = (STATUS_ORDER[a.status] ?? 4) - (STATUS_ORDER[b.status] ?? 4);
      if (diff !== 0) return diff;
      return (b.datecreated || '').localeCompare(a.datecreated || '');
    });
    if (!sortField) return base;
    return base.sort((a, b) => {
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [incidents, statusFilter, search, sortField, sortDir]);

  return (
    <div className="il-page">

      {/* Header */}
      <div className="il-header">
        <div>
          <h2 className="il-title">Incidents</h2>
          <p className="il-sub">Declare and manage emergency incidents. Click a row to open the command hub.</p>
        </div>
        <button className="il-new-btn" onClick={() => navigate('/incidents/new')}>
          <IconPlus size={14} /> Declare incident
        </button>
      </div>

      {/* Filters */}
      <div className="il-toolbar">
        <div className="il-search-wrap">
          <IconSearch size={14} className="il-search-icon" />
          <input
            className="il-search"
            placeholder="Search name, commander, location, type…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="il-filter-tabs">
          {(['all', 'active', 'monitoring', 'closed'] as const).map(s => (
            <button
              key={s}
              className={`il-filter-tab${statusFilter === s ? ' active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              <span className="il-filter-count">{statusCounts[s]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="il-empty">
          <IconCircleCheck size={36} color="var(--color-neutral-300)" />
          <p>No incidents found.</p>
        </div>
      ) : (
        <div className="il-table-card">
          <table className="il-table">
            <thead>
              <tr>
                <th className="il-th" style={{ width: 24 }}></th>
                {[
                  { label: 'Incident', field: 'name' },
                  { label: 'Type', field: 'incidentType' },
                  { label: 'Severity', field: 'severity' },
                  { label: 'Location', field: 'location' },
                  { label: 'IC', field: 'commander' },
                  { label: 'Elapsed', field: 'startDate' },
                ].map(col => (
                  <th
                    key={col.label}
                    className="il-th"
                    onClick={() => toggleSort(col.field)}
                    style={{
                      cursor: 'pointer',
                      userSelect: 'none',
                      color: sortField === col.field ? '#4B3FFF' : undefined,
                    }}>
                    {col.label}
                    <span style={{ marginLeft: 4, opacity: sortField === col.field ? 1 : 0.25 }}>
                      {sortField === col.field && sortDir === 'desc' ? '↓' : '↑'}
                    </span>
                  </th>
                ))}
                <th className="il-th" style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inc => {
                const isActive = activeIncident?.id === inc.id;
                const sev = severityColor((inc as unknown as Record<string, unknown>).severity as string);
                const sevLabel = ((inc as unknown as Record<string, unknown>).severity as string || '').toUpperCase();
                const statusDot = inc.status === 'active' ? 'active' : inc.status === 'monitoring' ? 'monitoring' : 'closed';
                return (
                  <tr
                    key={inc.id}
                    className="il-tr"
                    onClick={() => navigate(`/incidents/${inc.id}`)}
                  >
                    <td className="il-td" onClick={e => e.stopPropagation()}>
                      <span className={`il-dot il-dot--${statusDot}`} />
                    </td>
                    <td className="il-td">
                      <span className="il-name">{inc.name}</span>
                      {isActive && <span className="il-active-badge">ACTIVE CTX</span>}
                    </td>
                    <td className="il-td il-td--muted">
                      {(() => {
                        const raw = inc.incidentType || String((inc as any).type || '');
                        const t = raw.toLowerCase();
                        const label = INCIDENT_TYPE_META[t]?.label;
                        if (label) return label;
                        return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—';
                      })()}
                    </td>
                    <td className="il-td">
                      {sevLabel ? (
                        <span className="il-sev-chip" style={{ background: sev.bg, color: sev.text }}>
                          {sevLabel}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="il-td il-td--muted">{inc.location || '—'}</td>
                    <td className="il-td il-td--muted">{inc.commander || '—'}</td>
                    <td className="il-td il-td--muted">{elapsed(inc.startDate) || '—'}</td>
                    <td className="il-td" onClick={e => e.stopPropagation()} style={{ textAlign: 'right' }}>
                      {!isActive && (
                        <button className="il-set-active-btn" onClick={() => setActiveIncident(inc as Incident)}>
                          Set Active
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="il-footer">{filtered.length} incident{filtered.length !== 1 ? 's' : ''}</div>
        </div>
      )}
    </div>
  );
}
